import {useCallback, useRef} from 'react'
import debounce from 'lodash/debounce.js'
import classNames from 'classnames'

import {getState, updateForm, formsSelector, useAutoForm} from '../../store.js'
import Icon from '../icons/Icon.js'
import TextInput from './TextInput.js'
import Zropdown, {useZropdown} from './Zropdown.js'
import apiverson from '../apiverson.js'
import {setStocktake} from '../../data/stocktakes.js'
import Quantity from './Quantity.js'

export function getInitialForm() {
  return {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    isLoading: false,
    serverError: null,
  }
}

export async function searchStocktakes(
  dropdown,
  excludedReferenceIDs = [],
  queryParams = {},
) {
  const taskQueue = searchStocktakes.taskQueue || {}
  searchStocktakes.taskQueue = taskQueue

  taskQueue[dropdown] = {}

  try {
    const form = formsSelector(getState())[dropdown]

    if (!form) {
      return
    }

    const text = form.text.trim()

    if (!text) {
      updateForm(dropdown, {
        results: [],
        selectedIndex: 0,
        hasFoundNothing: false,
        isLoading: false,
        serverError: null,
      })

      return
    }

    updateForm(dropdown, {isLoading: true, serverError: null})

    const limit = 15 + excludedReferenceIDs.length

    const {
      json: {stocktake: stocktakes},
    } = await apiverson.get('/stocktake', {
      limit,
      search: form.text,
      ...queryParams,
    })

    if (searchStocktakes.taskQueue[dropdown] !== taskQueue[dropdown]) {
      return
    }

    const results = stocktakes.filter(
      ({reference_id}) => !excludedReferenceIDs.includes(reference_id),
    )

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
      isLoading: false,
    })
  } catch (err) {
    updateForm(dropdown, {
      results: [],
      selectedIndex: 0,
      serverError: err.message,
      isLoading: false,
    })
  } finally {
    if (searchStocktakes.taskQueue[dropdown] === taskQueue[dropdown]) {
      delete taskQueue[dropdown]
    }
  }
}

/**
 * Component to find a Stocktake
 *
 * @param {Object} props
 * @param {React.ReactElement | String} [props.label]
 * @param {String} [props.placeholder]
 * @param {String} props.dropdown
 * @param {Function} props.onSelect
 * @param {Array} [props.excludedReferenceIDs]
 * @param {Object} [props.queryParams]
 * @param {Bool} [props.autoFocus]
 * @param {Function} [props.onBlur]
 *
 * @returns {React.ReactElement}
 */
export default function StocktakeFilter({
  label,
  placeholder,
  dropdown,
  onSelect,
  excludedReferenceIDs,
  queryParams,
  autoFocus,
  onBlur,
}) {
  const id = `${dropdown}__input`
  placeholder = placeholder || 'Search for a stocktake...'

  const debouncedSearchStocktakes = useCallback(
    debounce(
      () => searchStocktakes(dropdown, excludedReferenceIDs, queryParams),
      400,
    ),
    [dropdown, excludedReferenceIDs, queryParams],
  )

  const ref = useRef()

  const form = useAutoForm(dropdown, getInitialForm())

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onChange: () => debouncedSearchStocktakes(),
      onSelect: (stocktake) => {
        setStocktake(stocktake)

        onSelect(stocktake)

        onChangeInternal('')
      },
    })

  return (
    <div className="outerwrap--stocktake-search-zropdown">
      {label && (
        <label className="label--stocktake-search" htmlFor={id}>
          {label}
        </label>
      )}
      <div className="wrap--stocktake-search" ref={ref}>
        <label className="label--input-prefix label--search" htmlFor={id}>
          <Icon name="search" className="icon--input-prefix" />
          <TextInput
            className="input--search input--lighter inline-block v-align-middle"
            id={id}
            value={form.text}
            placeholder={placeholder}
            onChange={(value) => onChangeInternal(value)}
            onBlur={(event) => onBlur && onBlur(event)}
            autoFocus={autoFocus}
          />
        </label>
      </div>
      <Zropdown
        parentRef={ref}
        dropdown={dropdown}
        form={form}
        onConfirm={onConfirm}
        onUp={onUp}
        onDown={onDown}
      >
        {form.isLoading && (
          <li className="list__item--dropdown list__item--dropdown-empty align-center">
            <span className="spinner--sm v-align-middle" />
            <strong className="inline-block v-align-middle fs-00 op-75 margin-left-5">
              Loading...
            </strong>
          </li>
        )}
        {form.results.map((stocktake, index) => {
          return (
            <li
              key={stocktake.reference_id}
              className="list__item list__item--dropdown"
            >
              <button
                key={stocktake.id}
                className={classNames(
                  'btn--link list__link list__link--dropdown flex--justify-nowrap',
                  {
                    'list__link--dropdown-arrow-focus':
                      index === form.selectedIndex,
                  },
                )}
                onClick={() => onSelectInternal(stocktake)}
              >
                <div>
                  <strong className="block">{stocktake.reference_id}</strong>
                  <span className="block unbold">
                    SKU Count: <Quantity value={stocktake.line_count.total} />
                  </span>
                </div>
              </button>
            </li>
          )
        })}
        {form.hasFoundNothing && (
          <li className="list__item--dropdown list__item--dropdown-empty align-center">
            <em>No stocktakes were found</em>
          </li>
        )}
      </Zropdown>
    </div>
  )
}
